/**
 * @file S3Browser.tsx - S3 File Browser Component
 *
 * Main component for browsing and managing files in the user's S3 workspace.
 * Provides a hierarchical folder navigation interface with file operations.
 *
 * Features:
 * - Folder navigation with breadcrumb path display
 * - File upload (local .excalidraw files to S3)
 * - Save current scene to S3 with custom name
 * - Create new folders
 * - Delete files and folders
 * - Download/load files from S3 into editor
 *
 * Integration:
 * - Uses API.ts for all S3 operations (authenticated via JWT)
 * - Uses actionLoadSceneS3 action to load scenes into editor
 * - Stores scene data via setS3SceneData for saving operations
 *
 * Security:
 * - All operations require valid JWT token in localStorage
 * - Files are scoped to authenticated user's namespace
 */
import React, { useEffect, useState, useMemo } from "react";
import {
  listObjects,
  uploadFile,
  deleteObject,
  createFolder,
} from "excalidraw-app/data/API";

import {
  FiFolder,
  FiFile,
  FiUpload,
  FiTrash2,
  FiDownload,
  FiArrowUp,
  FiPlus,
} from "react-icons/fi";

import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";

import { useExcalidrawActionManager } from "@excalidraw/excalidraw/components/App";
import { actionLoadSceneS3 } from "@excalidraw/excalidraw/actions/actionExport";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import "./S3Browser.scss";

/**
 * Structure returned by listObjects API.
 * Contains filtered files and folders for current prefix.
 */
interface S3Listing {
  files: string[];
  folders: string[];
}

const FileItem: React.FC<{
  name: string;
  isFolder: boolean;
  currentPrefix: string;
  handleNavigate: (name: string) => void;
  handleDelete: (name: string, isFolder: boolean) => Promise<void>;
  handleDownload: (name: string) => Promise<void>;
}> = ({ name, isFolder, handleNavigate, handleDelete, handleDownload }) => (
  <li className="s3-browser__list__item">
    <div
      className="s3-browser__list__item__info"
      onClick={() => (isFolder ? handleNavigate(name) : handleDownload(name))}
    >
      {isFolder ? (
        <FiFolder size={18} className="icon-folder" />
      ) : (
        <FiFile size={18} className="icon-file" />
      )}
      <span>{name}</span>
    </div>

    <div className="s3-browser__list__item__actions">
      {!isFolder && (
        <button
          className="btn-action btn-action--download"
          title="Download"
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(name);
          }}
        >
          <FiDownload size={16} />
        </button>
      )}
      <button
        className="btn-action btn-action--delete"
        title="Delete"
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(name, isFolder);
        }}
      >
        <FiTrash2 size={16} />
      </button>
    </div>
  </li>
);

/**
 * Module-level storage for current scene data.
 * Used to pass scene elements to S3 save operation.
 * Set by saveElements action before opening S3 browser.
 */
let S3elements: readonly ExcalidrawElement[] = [];
let S3appState = {};
let S3files = {};

/**
 * Sets the current scene data for S3 save operations.
 * Called from saveElements action to cache scene before saving.
 *
 * @param elements - Current canvas elements
 * @param appState - Current application state
 * @param files - Binary files (images) in the scene
 */
export function setS3SceneData(
  elements: readonly ExcalidrawElement[],
  appState: any,
  files: any,
) {
  S3elements = elements;
  S3appState = appState;
  S3files = files;
}

interface S3BrowserProps {
  onClose: () => void;
}

/**
 * Main S3 file browser component.
 * Renders folder tree, file list, and action buttons.
 *
 * @param onClose - Callback to close the browser modal
 */
export function S3Browser({ onClose }: S3BrowserProps) {
  const actionManager = useExcalidrawActionManager();

  const [listing, setListing] = useState<S3Listing>({ files: [], folders: [] });
  const [currentPrefix, setCurrentPrefix] = useState("");
  const [uploading, setUploading] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [currentSceneName, setCurrentSceneName] = useState("");

  const USER_DISPLAY_PATH = "/draw/";

  async function loadObjects(prefix: string) {
    try {
      const data = await listObjects(prefix);
      setListing(data);
    } catch (error) {
      console.error("Error loading S3 objects:", error);
    }
  }

  useEffect(() => {
    loadObjects(currentPrefix);
  }, [currentPrefix]);

  const parentFolder = useMemo(() => {
    if (currentPrefix === "") {
      return null;
    }
    const parent = currentPrefix.replace(/\/$/, "");
    const lastSlash = parent.lastIndexOf("/");
    // If no "/", then parent is root
    if (lastSlash === -1) {
      return "";
    }
    return parent.substring(0, lastSlash + 1);
  }, [currentPrefix]);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) {
      return;
    }
    setUploading(true);

    try {
      for (const file of e.target.files) {
        const key = currentPrefix + file.name;
        let content = await file.text();
        content = JSON.parse(content);
        await uploadFile(key, content);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }

    setUploading(false);
    await loadObjects(currentPrefix);
  }

  async function handleDelete(name: string, isFolder: boolean) {
    if (
      !window.confirm(
        `Do you really want to delete ${name}${
          isFolder ? " and it's content ?" : "?"
        }`,
      )
    ) {
      return;
    }

    const keyToDelete = currentPrefix + name + (isFolder ? "/" : "");

    try {
      await deleteObject(keyToDelete);
      await loadObjects(currentPrefix);
    } catch (error) {
      console.error("Deletion failed:", error);
    }
  }

  async function handleCreateFolder() {
    if (!newFolderName.trim()) {
      return;
    }

    const folderKey = `${currentPrefix + newFolderName.trim()}`;

    try {
      await createFolder(folderKey);
      setNewFolderName("");
      await loadObjects(currentPrefix);
    } catch (error) {
      console.error("Folder creation failed:", error);
    }
  }

  function handleNavigate(name: string) {
    setCurrentPrefix(`${currentPrefix + name}/`);
  }

  function handleNavigateUp() {
    if (parentFolder !== null) {
      setCurrentPrefix(parentFolder);
    }
  }

  async function handleDownload(name: string) {
    const fullKey = currentPrefix + name;
    try {
      actionManager.executeAction(actionLoadSceneS3, "api", fullKey);
      onClose();
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to load the file from S3.");
    }
  }

  async function handleSaveScene() {
    if (!currentSceneName.trim()) {
      return;
    }

    const key = `${currentPrefix + currentSceneName.trim()}${
      currentSceneName.includes(".excalidraw") ? "" : ".excalidraw"
    }`;
    const serialized = serializeAsJSON(
      S3elements,
      {
        ...S3appState,
        fileHandle: null,
      },
      S3files,
      "local",
    );
    try {
      await uploadFile(key, serialized);
      await loadObjects(currentPrefix);
      setCurrentSceneName("");
    } catch (error) {
      console.error("Save scene failed:", error);
    }
  }

  return (
    <div className="s3-browser-container">
      <div className="s3-browser">
        {/* 1. Header */}
        <div className="s3-browser__header">
          <h1>File browser</h1>
        </div>

        {/* 2. Path Breadcrumb/Display */}
        <div className="s3-browser__path">
          {parentFolder !== null && (
            <button
              onClick={handleNavigateUp}
              className="btn-up"
              title="Parent Folder"
            >
              <FiArrowUp size={14} />
            </button>
          )}
          <span className="s3-browser__path__display">
            {USER_DISPLAY_PATH}
            <span className="s3-browser__path__current">{currentPrefix}</span>
          </span>
        </div>

        {/* 3. Controls: Upload & Create Folder */}
        <div className="s3-browser__controls">
          {/* File Upload Button */}
          <div className="s3-browser__controls__upload">
            <input
              id="file-upload"
              type="file"
              multiple
              onChange={handleUpload}
              className="input-hidden"
              disabled={uploading}
            />
            <label
              htmlFor="file-upload"
              className={`btn btn-primary ${uploading ? "btn--disabled" : ""}`}
            >
              <FiUpload size={16} />
              <span>{uploading ? "Uploading..." : "Upload Files"}</span>
            </label>
          </div>

          {/* Scene Upload Input and Button */}
          <div className="s3-browser__controls__create-folder">
            <input
              type="text"
              placeholder="File name"
              value={currentSceneName}
              onChange={(e) => setCurrentSceneName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveScene();
                }
              }}
            />
            <button
              onClick={handleSaveScene}
              className="btn btn-secondary"
              disabled={!currentSceneName.trim()}
              title="Save current scene"
            >
              <FiPlus size={18} />
            </button>
          </div>

          {/* Create Folder Input and Button */}
          <div className="s3-browser__controls__create-folder">
            <input
              type="text"
              placeholder="New folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleCreateFolder();
                }
              }}
            />
            <button
              onClick={handleCreateFolder}
              className="btn btn-secondary"
              disabled={!newFolderName.trim()}
              title="Create Folder"
            >
              <FiPlus size={18} />
            </button>
          </div>
        </div>

        {/* 4. File/Folder List */}
        <div className="s3-browser__list-container">
          {listing.folders.length === 0 && listing.files.length === 0 ? (
            <p className="s3-browser__empty-message">This folder is empty.</p>
          ) : (
            <ul className="s3-browser__list">
              {listing.folders.map((f) => (
                <FileItem
                  key={`folder-${f}`}
                  name={f}
                  isFolder={true}
                  currentPrefix={currentPrefix}
                  handleNavigate={handleNavigate}
                  handleDelete={handleDelete}
                  handleDownload={handleDownload}
                />
              ))}
              {listing.files.map((f) => (
                <FileItem
                  key={`file-${f}`}
                  name={f}
                  isFolder={false}
                  currentPrefix={currentPrefix}
                  handleNavigate={handleNavigate}
                  handleDelete={handleDelete}
                  handleDownload={handleDownload}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
